import React, { useState, useRef } from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import { optimizeVideoUrl } from '../../utils/videoLinkOptimize';
import { isVideo } from '../../utils/isVideo';
import { useMediaQuery } from '../../hooks/mediaQuery';
import { useRouter } from 'next/router';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { type Swiper as SwiperType } from 'swiper';
import {
  LeftSwipperArrows,
  RightSwipperArrows
} from '../../shared-components/SwipperArrows/SwipperArrows';
import MobileGallery from './MobileGallery';
SwiperCore.use([Navigation]);

const StyledVideo = styled.video``;

const NavigationButton = styled.div`
  background: #e2e2e2;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
`;

interface GalleryProps {
  media: {
    url: string;
    alt: string;
    name: string;
  }[];
  productName?: string;
  productSlug?: string;
  cardMessage?: string;
  showMobileGallery: boolean;
  setShowMobileGallery: (show: boolean) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}
const StyledSwiper = styled(Swiper)`
  .swiper-button-prev,
  .swiper-button-next {
    top: 50%; /* Center the buttons vertically */
    transform: translateY(-50%);
    width: 40px; /* Reduce width */
    height: 40px; /* Reduce height */
    color: #757575;
    border: 1px solid #757575; /* Add border */
    border-radius: 50%; /* Make it circular */
    display: flex; /* Center the icon */
    align-items: center; /* Center the icon */
    justify-content: center; /* Center the icon */
    font-size: 14px; /* Adjust the font size for smaller arrow icons */

    &::after {
      display: contents;
      position: absolute;
      font-size: 1rem;
      transform: translate(-50%, -50%);
      border-style: solid;
      border-color: #fff;
      z-index: 50;
    }
  }
`;
const PhotosHeader = ({
  media,
  productName,
  productSlug,
  cardMessage,
  showMobileGallery,
  setShowMobileGallery,
  isOpen,
  setIsOpen
}: GalleryProps) => {
  const mediaQuery = useMediaQuery(640);
  const prevRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLDivElement>(null);

  const onBeforeInit = (Swiper: SwiperType): void => {
    if (typeof Swiper.params.navigation !== 'boolean') {
      const navigation = Swiper.params.navigation;
      if (navigation) {
        navigation.prevEl = prevRef.current;
        navigation.nextEl = nextRef.current;
      }
    }
  };

  // Modal state
  // const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState<boolean>(true);
  const [opacity, setOpacity] = useState(0);
  const [isLastSlide, setIsLastSlide] = useState(false);
  const [isFirstSlide, setIsFirstSlide] = useState(false);

  const handleSlideChange = (swiper: any) => {
    setActiveIndex(swiper.activeIndex);
    setIsLastSlide(swiper.isEnd);
    setIsFirstSlide(swiper.isBeginning);
  };
  const handleSwiperInit = (swiper: any) => {
    setIsFirstSlide(swiper.isBeginning);
    setIsLastSlide(swiper.isEnd);
  };
  const handleImageClick = (index: number) => {
    if (!mediaQuery || showMobileGallery) {
      setActiveIndex(index);
      setIsOpen(true);
      setImageLoading(true);
    }
  };

  const handleShowAllClick = () => {
    if (mediaQuery) {
      // Mobile: Show mobile gallery
      setShowMobileGallery(true);
    } else {
      // Desktop: Show modal
      setActiveIndex(0);
      setIsOpen(true);
      setImageLoading(true);
    }
  };

  const closeMobileGallery = () => {
    setShowMobileGallery(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeModal = () => {
    setIsOpen(false);
    setImageLoading(false);
  };

  const handleImageLoad = () => {
    setImageLoading(false);
    let currentOpacity = 0.2;
    const opacityInterval = setInterval(() => {
      if (currentOpacity < 1) {
        currentOpacity += 0.2;
        setOpacity(currentOpacity);
      } else {
        clearInterval(opacityInterval);
      }
    }, 400);
  };
  return (
    <>
      <div className="relative flex w-full mx-auto">
        {cardMessage && cardMessage === 'AWARD WINNER!' && (
          <div className="absolute z-30 max-w-[300px] lg:max-w-[250px] py-2.5 px-2.5 md:top-2 md:left-2 xl:top-4 xl:left-4">
            <Image
              src="/award-winner.png"
              width={100}
              height={100}
              quality={100}
              alt="award-winner-logo"
              className="w-[70px] h-[76px] xl:w-[90px] xl:h-[106px]"
            />
          </div>
        )}

        <div
          onClick={handleShowAllClick}
          className={`absolute ${
            media && media[0] && isVideo(media[0]?.url)
              ? 'bottom-[21px] right-[14px]'
              : 'bottom-[20px] right-[20px]'
          } flex justify-center items-center z-10`}
        >
          <button className="flex items-center gap-1 py-2 px-4 h-[36px] sm:h-[44px] bg-white border text-secondary font-semibold rounded-full shadow-lg hover:shadow-xl transition text-base">
            <Image
              src={'/gallery.svg'}
              width={24}
              height={24}
              alt="local"
              className="mr-2"
            />
            <span className="text-sm md:text-base">
              {!mediaQuery ? `See all ${media?.length} photos` : 'See all'}
            </span>
          </button>
        </div>
        <div className="h-[304px] md:h-[525px] relative w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div
              className="w-full h-[304px] md:h-[525px] cursor-pointer"
              onClick={() => handleImageClick(0)}
            >
              {media?.length > 0 &&
                media[0] &&
                (!isVideo(media[0].url) ? (
                  <Image
                    src={media[0]?.url}
                    alt={media[0]?.alt || `${productName} image 1`}
                    quality={80}
                    fetchPriority="high"
                    width={1000}
                    height={420}
                    loading="eager"
                    decoding="async"
                    className="h-[304px] md:h-[525px] object-cover w-full rounded-[12px] md:rounded-l-[12px]"
                    placeholder="blur"
                    blurDataURL="/placeholder.webp"
                    priority={true}
                  />
                ) : (
                  <StyledVideo
                    playsInline
                    autoPlay={!mediaQuery}
                    controls={mediaQuery}
                    muted
                    loop={!mediaQuery}
                    className="h-[304px] md:h-[525px] w-full object-cover rounded-[8px]"
                    src={optimizeVideoUrl(media[0]?.url)}
                  />
                ))}
            </div>

            <div className="w-full hidden md:grid h-[525px] grid-cols-2 grid-rows-2 gap-3">
              {media?.slice(1, 5)?.map((mediaItem, index) => {
                const classNames =
                  index === 1
                    ? 'rounded-tr-[12px]'
                    : index === 3
                    ? 'rounded-br-[12px]'
                    : '';
                return (
                  <div
                    key={mediaItem?.url}
                    onClick={() => handleImageClick(index + 1)}
                    className="cursor-pointer"
                  >
                    {!isVideo(mediaItem?.url) ? (
                      <Image
                        src={mediaItem?.url}
                        alt={
                          mediaItem?.alt || `${productName} image ${index + 2}`
                        }
                        quality={70}
                        width={600}
                        height={310}
                        className={`h-[258px] object-cover w-full ${classNames}`}
                        placeholder="blur"
                        blurDataURL="/placeholder.webp"
                        priority={index === 0}
                      />
                    ) : (
                      <StyledVideo
                        playsInline
                        autoPlay={!mediaQuery}
                        controls={mediaQuery}
                        muted
                        loop={!mediaQuery}
                        className={`h-[258px] w-full object-cover ${classNames}`}
                        src={optimizeVideoUrl(mediaItem.url)}
                        onClick={e => e.preventDefault()}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Gallery */}
      {showMobileGallery && (
        <MobileGallery
          media={media}
          productName={productName}
          onClose={closeMobileGallery}
          onImageClick={handleImageClick}
        />
      )}

      {isOpen && (
        <>
          {/* Mobile Modal */}
          {mediaQuery && (
            <div className="fixed inset-0 w-full h-full bg-black bg-opacity-95 flex items-center justify-center z-[10000]">
              <div className="w-[90%] max-w-md bg-black bg-opacity-70 rounded-lg p-4 flex flex-col gap-4">
                <div className="w-full flex items-center justify-between">
                  <span className="text-white text-lg font-semibold">{`${
                    activeIndex + 1
                  } of ${media?.length}`}</span>
                  <button
                    onClick={closeModal}
                    className="flex items-center gap-2"
                  >
                    <Image
                      src={'/cross-border-icon.svg'}
                      alt="close-icon"
                      width={24}
                      height={24}
                    />
                  </button>
                </div>

                <div className="relative w-full flex items-center justify-center">
                  <Swiper
                    initialSlide={activeIndex}
                    spaceBetween={10}
                    slidesPerView={1}
                    loop={false}
                    onSlideChange={handleSlideChange}
                    onInit={handleSwiperInit}
                    navigation={{ prevEl: '.prevSlide', nextEl: '.nextSlide' }}
                  >
                    {media?.map((mediaItem, index) => (
                      <SwiperSlide
                        key={index}
                        className="flex items-center justify-center"
                      >
                        {!isVideo(mediaItem?.url) ? (
                          <Image
                            src={mediaItem?.url}
                            alt={mediaItem?.alt || mediaItem?.name}
                            width={400}
                            height={300}
                            className="object-cover w-full h-[40vh] rounded-lg"
                            style={{
                              opacity: imageLoading ? 0 : 1,
                              transition: 'opacity 0.4s ease-in-out'
                            }}
                            priority={index === 0}
                            onLoadingComplete={handleImageLoad}
                          />
                        ) : (
                          <video
                            src={optimizeVideoUrl(mediaItem?.url)}
                            className="object-cover w-full h-[40vh] rounded-lg"
                            playsInline
                            autoPlay={!mediaQuery}
                            controls={mediaQuery}
                            muted
                            loop={!mediaQuery}
                          />
                        )}
                      </SwiperSlide>
                    ))}
                  </Swiper>
                  <div
                    id="slider-btn"
                    className="absolute top-1/2 left-0 right-0 flex justify-between px-[4px] z-[50]"
                  >
                    <div className="prevSlide cursor-pointer">
                      <LeftSwipperArrows isFirstSlide={isFirstSlide} />
                    </div>
                    <div className="nextSlide cursor-pointer">
                      <RightSwipperArrows isLastSlide={isLastSlide} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Desktop Modal - Original Design */}
          {!mediaQuery && (
            <div className="fixed top-0 inset-0 w-full h-full gap-4 bg-black bg-opacity-95 flex flex-col items-center justify-center z-[10000]">
              <div className="w-[90%] flex items-start justify-end">
                <span className="text-white text-lg font-semibold text-center w-full block">{`${
                  activeIndex + 1
                }/${media?.length}`}</span>
                <button
                  onClick={closeModal}
                  className="flex items-center gap-2"
                >
                  <Image
                    src={'/cross-border-icon.svg'}
                    alt="close-icon"
                    width={32}
                    height={32}
                  />
                </button>
              </div>
              <div className="w-full flex items-center justify-center">
                <StyledSwiper
                  initialSlide={activeIndex}
                  spaceBetween={10}
                  slidesPerView={1}
                  loop={false}
                  navigation
                  onSlideChange={swiper => setActiveIndex(swiper.activeIndex)}
                >
                  {media?.map((mediaItem, index) => (
                    <SwiperSlide
                      key={index}
                      className="flex items-center justify-center"
                    >
                      {!isVideo(mediaItem?.url) ? (
                        <Image
                          src={mediaItem?.url}
                          alt={mediaItem?.alt || mediaItem?.name}
                          width={1500}
                          height={1000}
                          className="object-cover w-[70%] h-[80vh] mx-auto"
                          style={{
                            opacity: imageLoading ? 0 : 1,
                            transition: 'opacity 0.4s ease-in-out'
                          }}
                          priority={index === 0}
                          onLoadingComplete={handleImageLoad}
                        />
                      ) : (
                        <video
                          src={optimizeVideoUrl(mediaItem?.url)}
                          width={1500}
                          height={1000}
                          className="object-cover w-[70%] h-[80vh] mx-auto"
                          playsInline
                          autoPlay={!mediaQuery}
                          controls={mediaQuery}
                          muted
                          loop={!mediaQuery}
                        />
                      )}
                    </SwiperSlide>
                  ))}
                </StyledSwiper>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default PhotosHeader;
